/**
 * Pickup-slot countdown/status helpers. Same conventions as carrierCutoffStatus.ts:
 * Baghdad wall-clock, pure functions, caller injects `now` for testability.
 * Slot close instants come from the backend as ISO strings (close_instant).
 */

import type { PickupSlotState } from '../services/pickupSlots.api';

const TIMEZONE = 'Asia/Baghdad';
const DAY_START_MINUTES = 240; // operational day starts 04:00 Baghdad

export type SlotCloseState = 'ok' | 'approaching' | 'closing' | 'closed';

export interface SlotCloseStatus {
  state: SlotCloseState;
  label: string;
  minutesLeft: number | null;
}

/** Baghdad calendar date + minutes-of-day for an instant (Intl-based, DST-free zone). */
function baghdadParts(now: Date): { date: string; minutesOfDay: number } {
  const date = now.toLocaleDateString('en-CA', { timeZone: TIMEZONE }); // YYYY-MM-DD
  const hhmm = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: TIMEZONE });
  const [h, m] = hhmm.split(':').map(Number);
  return { date, minutesOfDay: h * 60 + m };
}

/** Operational date (04:00→04:00 Baghdad) an instant belongs to. */
export function getBaghdadOperationalDate(now: Date = new Date()): string {
  const { date, minutesOfDay } = baghdadParts(now);
  if (minutesOfDay >= DAY_START_MINUTES) return date;
  const [y, mo, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d - 1)).toISOString().slice(0, 10);
}

/** Countdown to a slot's close instant. closedAt (manual/auto close) always wins. */
export function getSlotCloseStatus(
  closeInstantIso: string,
  closedAt: string | null,
  now: Date = new Date()
): SlotCloseStatus {
  if (closedAt) {
    return { state: 'closed', label: 'Closed', minutesLeft: null };
  }
  const diffMin = Math.floor((new Date(closeInstantIso).getTime() - now.getTime()) / 60_000);
  if (diffMin < 0) {
    return { state: 'closed', label: 'Window ended', minutesLeft: 0 };
  }
  if (diffMin <= 30) {
    return { state: 'closing', label: `Closes in ${diffMin}m`, minutesLeft: diffMin };
  }
  if (diffMin <= 90) {
    return { state: 'approaching', label: `Closes in ${diffMin}m`, minutesLeft: diffMin };
  }
  const h = Math.floor(diffMin / 60);
  return { state: 'ok', label: `Closes in ${h}h ${diffMin % 60}m`, minutesLeft: diffMin };
}

export function getSlotCloseBadgeVariant(
  state: SlotCloseState
): 'success' | 'warning' | 'danger' | 'default' {
  switch (state) {
    case 'ok':
      return 'success';
    case 'approaching':
      return 'warning';
    case 'closing':
      return 'danger';
    default:
      return 'default';
  }
}

export const SLOT_STATE_LABELS: Record<string, string> = {
  slot_assigned: 'Awaiting routing',
  route_included: 'On route',
  route_appended: 'Appended',
  rolled_over: 'Rolled over',
  next_day_fallback: 'Next day',
  missed: 'Missed',
} satisfies Record<PickupSlotState, string>;

/**
 * Format a Date as a `datetime-local` input value using LOCAL (browser) wall-clock —
 * NOT Baghdad time like the rest of this file, and never `toISOString()` (UTC).
 * `<input type="datetime-local">` semantics are local wall-clock, so prefilling from
 * `toISOString()` silently skews by the browser's UTC offset (e.g. a Baghdad officer
 * would see a time 3 hours in the past). Round-trips via `new Date(value)` correctly
 * because the browser parses an offset-less datetime-local string as local time.
 */
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Tailwind classes per slot state (used on table badges). */
export function getSlotStateBadgeClasses(state: string): string {
  switch (state) {
    case 'slot_assigned':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'route_included':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'route_appended':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    case 'rolled_over':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'next_day_fallback':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'missed':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}
